import { useEffect, useRef } from "react";

/**
 * Polls a callback while a condition is true.
 * Uses setTimeout to prevent overlapping requests.
 *
 * @param {Function} callback - Async function to poll
 * @param {boolean} isActive - Whether polling should be active (e.g., modal is open)
 * @param {boolean} shouldPoll - Whether to continue polling after initial fetch (e.g., job is pending)
 * @param {number} interval - Polling interval in ms (default: 2000)
 * @param {Array} deps - Additional dependencies to trigger re-fetch
 */
export function usePolling(
  callback,
  isActive,
  shouldPoll,
  interval = 2000,
  deps = [],
) {
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isActive) return;

    const poll = async () => {
      await callback();

      if (isMountedRef.current && shouldPoll) {
        timeoutRef.current = setTimeout(poll, interval);
      }
    };

    poll();

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, shouldPoll, interval, ...deps]);
}
